// Powerlete — contextual gallery arrows + image-frame height.
// 1) Sets data-at-start / data-at-end on the closest <media-gallery> from the swipe
//    scroller's real scrollLeft, so the prev/next arrows can be hidden at the edges.
//    This is robust against iOS rubber-band overscroll (scrollLeft stays clamped at 0
//    / max during the bounce), unlike scroll-driven CSS which can flicker on the bounce.
// 2) Publishes the swipe image's real pixel height as --pw-frame-h on the gallery, so
//    the bottom model-size pill can be anchored a fixed gap above the IMAGE bottom
//    using the measured height rather than assuming a 4:5 ratio (cqi). This is robust
//    to whatever aspect ratio the slide actually renders at (framed / immersive /
//    natural), which a pure-CSS rule cannot read off a sibling element.
// Self-initialises via connectedCallback, so it re-runs when the gallery is
// re-rendered by the Section Rendering API (variant changes).
class PwGalleryEdges extends HTMLElement {
  connectedCallback() {
    this.gallery = this.closest('media-gallery');
    this.scroller = this.gallery && this.gallery.querySelector('slideshow-slides');
    if (!this.scroller) return;

    this._tick = () => {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.update();
      });
    };
    this._measure = () => this.setFrameHeight();
    this.scroller.addEventListener('scroll', this._tick, { passive: true });
    window.addEventListener('resize', this._tick, { passive: true });
    window.addEventListener('resize', this._measure, { passive: true });
    // The frame height depends on the rendered image, which may size after load.
    if (this._ro == null && 'ResizeObserver' in window) {
      this._ro = new ResizeObserver(this._measure);
      this._ro.observe(this.scroller);
    }
    this.update();
    this.setFrameHeight();
  }

  disconnectedCallback() {
    if (this.scroller) this.scroller.removeEventListener('scroll', this._tick);
    window.removeEventListener('resize', this._tick);
    window.removeEventListener('resize', this._measure);
    if (this._ro) this._ro.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  update() {
    const s = this.scroller;
    const max = s.scrollWidth - s.clientWidth;
    const x = Math.abs(s.scrollLeft); // abs() keeps it correct under RTL
    this.gallery.toggleAttribute('data-at-start', x <= 4);
    this.gallery.toggleAttribute('data-at-end', max <= 0 || x >= max - 4);
  }

  setFrameHeight() {
    const h = this.scroller.clientHeight;
    if (h > 0) this.gallery.style.setProperty('--pw-frame-h', h + 'px');
  }
}

if (!customElements.get('pw-gallery-edges')) {
  customElements.define('pw-gallery-edges', PwGalleryEdges);
}
